import { DisciplinaryCaseType } from "../../misc/types";
import InputField from "../input-field/InputField";
import DefaultButton from "../default-button/DefaultButton";
import DocumentText from "../../assets/icons/DocumentText";
import DisciplinaryDefaultCaseTable from "./DisciplinaryDefaultCaseTable";
import { useHookstate } from "@hookstate/core";
import { Demerit, DisciplinaryDefaultCaseTableState } from "../../types";
import { useUniversalFind } from "../../hooks/universal";
import DisciplinaryTableItem from "../disciplinary-table-item/DisciplinaryTableItem";
import Modal from "../Modal/Modal";
import {
  onInputChangeNative,
  showSnackbar,
  updateLocalStateRaw,
  userHasRole,
} from "../../misc/utils";
import ArrowLeft from "../../assets/icons/ArrowLeft";
import Tick from "../../assets/icons/Tick";
import { simpleGetter } from "../../actions/common";
import { demeritsApi } from "../../api/api";
import { HasRole } from "../HasRole";
import { DisciplinaryDemerit } from "./DisciplinaryDemerit";
import { useGlobalUserState } from "../../stores/user";

const categoryList = [
  "Тип нарушения",
  "Студент",
  "Дата создания",
  "Статус",
  "",
];

type DisciplinaryWarningsPropsType = {
  caseItem: DisciplinaryCaseType;
};
const DisciplinaryWarnings = ({ caseItem }: DisciplinaryWarningsPropsType) => {
  const userState = useGlobalUserState();
  const user = userState.get();
  const uniqUser = user.uniqUser || {};
  const localState = useHookstate<DisciplinaryDefaultCaseTableState>({});
  const state = localState.get();
  const checkedItems = state.checkedItems || [];
  const caseName = state.caseName || "";
  const selectedDemerit = state.selectedDemerit;
  const isAddDemeritModalOpen = Boolean(state.isAddDemeritModalOpen);
  const isAddCaseModalOpen = Boolean(state.isAddCaseModalOpen);

  // Get all demerits
  const allDemerits: Demerit[] = useUniversalFind(
    "Demerit",
    {},
    {
      include: [
        "main_user", // Keep this for backward compatibility
        "demerit_type",
        {
          model: "Media",
          as: "media",
          through: {
            attributes: ["label"],
          },
        },
      ],
      order: [["createdAt", "DESC"]],
    }
  );

  // Get all DemeritStudent records with student data
  const allDemeritStudents = useUniversalFind(
    "DemeritStudent",
    {},
    {
      include: [
        {
          model: "MainUser",
          as: "student",
        },
      ],
    }
  );

  // Group students by demerit_id
  const studentsByDemerit = allDemeritStudents.reduce<Record<number, any[]>>(
    (acc: any, ds: any) => {
      // todo
      if (!acc[ds.demerit_id]) {
        acc[ds.demerit_id] = [];
      }
      if (ds.student) {
        acc[ds.demerit_id].push(ds.student);
      }
      return acc;
    },
    {}
  );

  // Enhance demerits with students array
  const demeritsWithStudents = allDemerits.map((demerit) => ({
    ...demerit,
    students: studentsByDemerit[demerit.id] || [],
  }));

  const demerits = demeritsWithStudents
    .filter((warning) =>
      !userHasRole("view_demerits_extended")
        ? warning.created_by === uniqUser.id
        : true
    )
    .sort((a, b) => {
      const statusOrderA = a.status === 0 ? 0 : 1;
      const statusOrderB = b.status === 0 ? 0 : 1;
      if (statusOrderA !== statusOrderB) return statusOrderA - statusOrderB;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

  const selectableDemerits = demerits.filter((d) => d.status === 0);

  const onSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    localState.checkedItems.set(
      e.target.checked ? selectableDemerits.map((d) => d.id) : []
    );
  };

  const toggleCreateCaseModal = () => {
    localState.isAddCaseModalOpen.set(!isAddCaseModalOpen);
  };

  const validateForm = () => Boolean(caseName);

  const submit = async () => {
    const response = await simpleGetter(demeritsApi.createDisciplinaryCase, {
      name: caseName,
      demerits: checkedItems,
    });
    showSnackbar(
      response.success ? "Успешно" : "Произошла ошибка",
      response.success ? "success" : "error"
    );
    if (response.success) {
      toggleCreateCaseModal();
      localState.caseName.set("");
      localState.checkedItems.set([]);
    }
  };

  const onCheckboxCheck = (item: Demerit) => () => {
    if (item.status !== 0) return;
    if (checkedItems?.includes(item.id)) {
      localState.checkedItems.set(checkedItems.filter((i) => i !== item.id));
    } else {
      localState.checkedItems.merge([item.id]);
    }
  };

  const onViewClick = (item: Demerit) => () => {
    localState.selectedDemerit.set(item);
    localState.isAddDemeritModalOpen.set(true);
  };

  return (
    <div className='flex flex-col overflow-y-auto'>
      <div className='flex justify-between'>
        <InputField type='text' placeholder='Поиск по нарушениям' />
        <HasRole roles={["create_disciplinary_cases"]}>
          <DefaultButton
            Icon={DocumentText}
            additionalStyle='text-white bg-valley-main px-3 py-2'
            buttonText='Создать дело'
            disabled={!checkedItems.length}
            onClick={toggleCreateCaseModal}
          />
        </HasRole>
      </div>

      <table className='w-full flex-1 border-collapse mt-4 rounded-lg overflow-y-auto'>
        <thead>
          <tr className='bg-white'>
            <th key={-1} className='px-4 py-2 border-b text-left'>
              <input
                type='checkbox'
                checked={
                  Boolean(selectableDemerits.length) &&
                  selectableDemerits.length === checkedItems.length
                }
                onChange={onSelectAllChange}
                disabled={!selectableDemerits.length}
                title={
                  !selectableDemerits.length
                    ? "Нет активных выговоров для добавления в дело"
                    : ""
                }
              />
            </th>
            {categoryList.map((category, index) => (
              <th key={index} className='px-4 py-2 border-b text-left'>
                {category}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {demerits.map((item, index) => (
            <DisciplinaryTableItem
              onViewClick={onViewClick}
              onCheckboxCheck={onCheckboxCheck}
              isChecked={checkedItems.includes(item.id)}
              parentState={localState}
              key={index}
              item={item}
              index={index}
            />
          ))}
        </tbody>
      </table>

      <Modal
        additionalClassName='w-[32%] rounded-xl p-8 flex flex-col'
        visible={isAddCaseModalOpen}
        setVisible={updateLocalStateRaw(localState, "isAddCaseModalOpen")}
      >
        <div className='flex flex-col gap-8'>
          <span className='text-2xl text-valley-black leading-[26.4px]'>
            Создание дела
          </span>
          <InputField
            type='text'
            label='Название дела'
            id='case-name'
            value={caseName}
            handleChange={onInputChangeNative(localState, "caseName")}
            additionalClassName='bg-valley-background-blue rounded-lg py-3 px-4 mt-2'
          />
          <div className='flex flex-row gap-3'>
            <DefaultButton
              Icon={ArrowLeft}
              buttonText='Вернуться'
              additionalStyle='flex-1 bg-valley-gray text-white py-3'
              onClick={toggleCreateCaseModal}
            />
            <DefaultButton
              Icon={Tick}
              buttonText='Сохранить'
              additionalStyle='flex-1 bg-valley-main text-white py-3'
              disabled={!validateForm()}
              onClick={submit}
            />
          </div>
        </div>
      </Modal>
      <Modal
        additionalClassName='w-[52%] h-[90%] rounded-xl p-8 flex flex-col'
        visible={isAddDemeritModalOpen}
        setVisible={updateLocalStateRaw(localState, "isAddDemeritModalOpen")}
      >
        <DisciplinaryDemerit
          parentState={localState}
          selectedStudent={selectedDemerit?.main_user || null}
          mode='view'
        />
      </Modal>
    </div>
  );
};

export default DisciplinaryWarnings;
